import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowLeft, Save, Loader2, Sparkles, Wand2, ShieldCheck, Feather, FileDown,
  History, FolderLock, Users2, FileText, Send, PenLine, Columns3, Check, Clock, Eye, CheckCircle2, Copy, UserPlus, Trash2,
} from "lucide-react";
import { AppShell } from "@/components/app/AppShell";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import api from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const STANCE_ORDER = ["founder", "balanced", "restrictive"];
const STANCE_META = {
  founder: { label: "Founder-friendly", icon: Feather },
  balanced: { label: "Balanced", icon: ShieldCheck },
  restrictive: { label: "Restrictive", icon: ShieldCheck },
};

function renderText(text, vars) {
  return (text || "").replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (_, k) => {
    const v = vars?.[k];
    return v === undefined || v === "" ? `[${k.replace(/_/g, " ")}]` : String(v);
  });
}

export default function DocumentBuilderEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [template, setTemplate] = useState(null);
  const [catalog, setCatalog] = useState({ profiles: [], stances: {} });
  const [clauses, setClauses] = useState([]);
  const [variables, setVariables] = useState({});
  const [profile, setProfile] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiBusy, setAiBusy] = useState(null); // clause key currently AI-processing
  const [historyOpen, setHistoryOpen] = useState(false);
  const [versions, setVersions] = useState([]);
  const [signatures, setSignatures] = useState([]);
  const [sigOpen, setSigOpen] = useState(false);
  const [sigForm, setSigForm] = useState({ recipient_name: "", recipient_email: "", message: "", require_countersign: false, ordered: false, bulk: false, signers: [{ name: "", email: "" }, { name: "", email: "" }] });
  const [sendingSig, setSendingSig] = useState(false);
  const [compareKey, setCompareKey] = useState(null);

  const loadSignatures = useCallback(async () => {
    try { const { data } = await api.get(`/builder-documents/${id}/signatures`); setSignatures(data); }
    catch (e) { console.error("Failed to load signatures", e); }
  }, [id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data }, cat] = await Promise.all([
        api.get(`/builder-documents/${id}`),
        api.get(`/document-builder/catalog`).catch(() => ({ data: { profiles: [], stances: {} } })),
      ]);
      setDoc(data);
      setTemplate(data.template);
      setCatalog(cat.data);
      const latest = data.latest || {};
      const sorted = [...(latest.clauses || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
      setClauses(sorted);
      setVariables(latest.variables || {});
      setProfile(data.recipient_profile || "");
      setVersions(data.versions || []);
    } catch (err) {
      toast.error("Could not load document");
    } finally { setLoading(false); }
    loadSignatures();
  }, [id, loadSignatures]);
  useEffect(() => { load(); }, [load]);

  const variantFor = (key, stance) => {
    const c = template?.clauses?.find((x) => x.key === key);
    return c?.variants?.[stance];
  };

  const setClauseStance = (key, stance) => {
    setClauses((cs) => cs.map((c) => {
      if (c.key !== key) return c;
      const v = variantFor(key, stance);
      return { ...c, stance, text: v !== undefined ? v : c.text };
    }));
  };

  const setClauseText = (key, text) => setClauses((cs) => cs.map((c) => (c.key === key ? { ...c, text } : c)));

  const applyProfile = (pid) => {
    setProfile(pid);
    const stance = catalog.profiles.find((p) => p.id === pid)?.default_stance;
    if (!stance) return;
    setClauses((cs) => cs.map((c) => {
      const v = variantFor(c.key, stance);
      return { ...c, stance, text: v !== undefined ? v : c.text };
    }));
    toast.success(`Applied ${catalog.profiles.find((p) => p.id === pid)?.name} stance to all clauses`);
  };

  const runAi = async (clause, action) => {
    setAiBusy(clause.key + action);
    try {
      const { data } = await api.post(`/builder-documents/${id}/ai-clause`, {
        action, clause_title: clause.title, current_text: clause.text, stance: clause.stance,
      });
      if (data.text) { setClauseText(clause.key, data.text); toast.success("AI updated the clause"); }
    } catch (err) { toast.error(err?.response?.data?.detail || "AI drafting failed"); }
    finally { setAiBusy(null); }
  };

  const saveVersion = async () => {
    setSaving(true);
    try {
      await api.post(`/builder-documents/${id}/versions`, {
        clauses, variables, recipient_profile: profile || null, note: "",
      });
      toast.success("New version saved");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const download = async (format) => {
    try {
      const res = await api.get(`/builder-documents/${id}/export`, { params: { format }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(doc?.title || "document").replace(/[^a-zA-Z0-9]+/g, "_")}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch { toast.error("Export failed"); }
  };

  const saveToVault = async () => {
    try {
      const { data } = await api.post(`/builder-documents/${id}/save-to-vault`);
      toast.success(`Saved "${data.name}" to Documents vault`);
    } catch { toast.error("Could not save to vault"); }
  };

  const sendForSignature = async () => {
    setSendingSig(true);
    try {
      if (sigForm.bulk) {
        const recipients = sigForm.signers.map((s) => ({ name: s.name.trim(), email: s.email.trim() })).filter((s) => s.name && s.email);
        if (recipients.length < 1) { setSendingSig(false); return toast.error("Add at least one recipient with name and email"); }
        await api.post(`/builder-documents/${id}/signatures`, { message: sigForm.message, recipients });
        toast.success(`Sent to ${recipients.length} recipient${recipients.length !== 1 ? "s" : ""}, each with their own link`);
      } else if (sigForm.ordered) {
        const signers = sigForm.signers.map((s) => ({ name: s.name.trim(), email: s.email.trim() })).filter((s) => s.name && s.email);
        if (signers.length < 2) { setSendingSig(false); return toast.error("Add at least two signers with name and email"); }
        await api.post(`/builder-documents/${id}/signatures`, { message: sigForm.message, signers });
        toast.success(`Sent to ${signers[0].name} first - the rest sign in order`);
      } else {
        if (!sigForm.recipient_name.trim() || !sigForm.recipient_email.trim()) { setSendingSig(false); return toast.error("Name and email are required"); }
        await api.post(`/builder-documents/${id}/signatures`, {
          recipient_name: sigForm.recipient_name, recipient_email: sigForm.recipient_email,
          message: sigForm.message, require_countersign: sigForm.require_countersign,
        });
        toast.success(`Sent to ${sigForm.recipient_name} for signature`);
      }
      setSigOpen(false);
      setSigForm({ recipient_name: "", recipient_email: "", message: "", require_countersign: false, ordered: false, bulk: false, signers: [{ name: "", email: "" }, { name: "", email: "" }] });
      loadSignatures();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not send"); }
    finally { setSendingSig(false); }
  };

  const setSigner = (i, field, val) => setSigForm((f) => ({ ...f, signers: f.signers.map((s, idx) => (idx === i ? { ...s, [field]: val } : s)) }));
  const addSigner = () => setSigForm((f) => ({ ...f, signers: [...f.signers, { name: "", email: "" }] }));
  const removeSigner = (i) => setSigForm((f) => ({ ...f, signers: f.signers.filter((_, idx) => idx !== i) }));

  const cancelSignature = async (sid) => {
    try { await api.delete(`/builder-documents/${id}/signatures/${sid}`); loadSignatures(); }
    catch { toast.error("Could not remove"); }
  };

  const resendSignature = async (sid) => {
    try { await api.post(`/builder-documents/${id}/signatures/${sid}/resend`); toast.success("Signing link resent (simulated email)"); loadSignatures(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not resend"); }
  };

  const auditEvents = (s) => {
    const ev = [];
    if (s.sent_at) ev.push({ label: "Sent", at: s.sent_at });
    if (s.last_resent_at) ev.push({ label: "Reminder resent", at: s.last_resent_at });
    if (s.viewed_at) ev.push({ label: "Viewed by recipient", at: s.viewed_at });
    if (s.signed_at) ev.push({ label: `Signed by ${s.signer_name || s.recipient_name}`, at: s.signed_at });
    if (s.countersigned_at) ev.push({ label: `Countersigned by ${s.countersigner_name || "company"}`, at: s.countersigned_at });
    return ev.sort((a, b) => (a.at || "").localeCompare(b.at || ""));
  };

  const countersign = async (sid) => {
    try {
      await api.post(`/builder-documents/${id}/signatures/${sid}/countersign`, { signer_name: "" });
      toast.success("Countersigned - signed PDF saved to vault");
      loadSignatures();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not countersign"); }
  };

  const downloadSigned = async (sig) => {
    if (!sig.signed_document_id) return;
    try {
      const res = await api.get(`/documents/${sig.signed_document_id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a"); a.href = url;
      a.download = `${(doc?.title || "document").replace(/[^a-zA-Z0-9]+/g, "_")}_SIGNED.pdf`;
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
    } catch { toast.error("Could not download signed PDF"); }
  };

  const copyLink = (url) => { navigator.clipboard?.writeText(url); toast.success("Signing link copied"); };

  const compareClause = clauses.find((c) => c.key === compareKey);
  const compareTemplate = template?.clauses?.find((c) => c.key === compareKey);
  const applyVariant = (stance) => {
    if (!compareClause) return;
    setClauseStance(compareClause.key, stance);
    toast.success(`Applied ${STANCE_META[stance].label} wording`);
    setCompareKey(null);
  };

  const SIG_STATUS = {
    queued: { label: "Waiting turn", cls: "border-slate-200 bg-slate-50 text-slate-500", icon: Clock },
    sent: { label: "Sent", cls: "border-slate-200 bg-slate-50 text-slate-600", icon: Clock },
    viewed: { label: "Viewed", cls: "border-amber-200 bg-amber-50 text-amber-800", icon: Eye },
    awaiting_countersign: { label: "Your signature", cls: "border-amber-200 bg-amber-50 text-amber-800", icon: PenLine },
    signed: { label: "Signed", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
    countersigned: { label: "Completed", cls: "border-emerald-200 bg-emerald-50 text-emerald-700", icon: CheckCircle2 },
  };

  if (loading) {
    return <AppShell title="Document Builder"><div className="flex items-center justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div></AppShell>;
  }
  if (!doc) return <AppShell title="Document Builder"><p className="text-sm text-muted-foreground">Not found.</p></AppShell>;

  const vars = template?.variables || [];

  return (
    <AppShell
      title={doc.title}
      subtitle={`${doc.template_name} · version ${doc.current_version}`}
      actions={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setHistoryOpen(true)} data-testid="version-history-btn">
            <History className="h-4 w-4" /> v{doc.current_version}
          </Button>
          <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setSigOpen(true)} data-testid="send-signature-btn">
            <Send className="h-4 w-4" /> Send for signature
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-1.5" data-testid="export-menu-btn"><FileDown className="h-4 w-4" /> Export</Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => download("pdf")} data-testid="export-pdf"><FileText className="mr-2 h-4 w-4" /> Download PDF</DropdownMenuItem>
              <DropdownMenuItem onClick={() => download("docx")} data-testid="export-docx"><FileText className="mr-2 h-4 w-4" /> Download DOCX</DropdownMenuItem>
              <DropdownMenuItem onClick={saveToVault} data-testid="save-to-vault"><FolderLock className="mr-2 h-4 w-4" /> Save to Documents vault</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button size="sm" className="gap-1.5" onClick={saveVersion} disabled={saving} data-testid="save-version-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save version
          </Button>
        </div>
      }
    >
      <button className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" onClick={() => navigate("/app/document-builder")} data-testid="back-to-list">
        <ArrowLeft className="h-4 w-4" /> All documents
      </button>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[320px_1fr]">
        {/* Left rail: profile + variables */}
        <div className="space-y-4">
          <Card className="rounded-xl border bg-card p-4">
            <div className="mb-2 flex items-center gap-2 text-sm font-semibold"><Users2 className="h-4 w-4 text-primary" /> Recipient profile</div>
            <Select value={profile} onValueChange={applyProfile}>
              <SelectTrigger data-testid="editor-profile-select"><SelectValue placeholder="Set overall stance…" /></SelectTrigger>
              <SelectContent>
                {catalog.profiles.map((p) => (
                  <SelectItem key={p.id} value={p.id} data-testid={`editor-profile-${p.id}`}>{p.name} - {catalog.stances[p.default_stance]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="mt-2 text-xs text-muted-foreground">Applying a profile sets every clause to that stance. You can still fine-tune each clause below.</p>
          </Card>

          <Card className="rounded-xl border bg-card p-4">
            <div className="mb-3 text-sm font-semibold text-foreground">Variables</div>
            <div className="space-y-3">
              {vars.map((v) => (
                <div key={v.key}>
                  <Label className="text-xs">{v.label}</Label>
                  <Input
                    value={variables[v.key] || ""}
                    onChange={(e) => setVariables((prev) => ({ ...prev, [v.key]: e.target.value }))}
                    placeholder={v.placeholder}
                    className="mt-1"
                    data-testid={`var-${v.key}`}
                  />
                </div>
              ))}
            </div>
          </Card>

          <Card className="rounded-xl border bg-card p-4">
            <div className="mb-2 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm font-semibold"><PenLine className="h-4 w-4 text-primary" /> Signatures</div>
              <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => setSigOpen(true)} data-testid="rail-send-signature"><Send className="h-3.5 w-3.5" /> Send</Button>
            </div>
            {signatures.length === 0 ? (
              <p className="text-xs text-muted-foreground">No signature requests yet. Send this document to a counterparty to review and sign.</p>
            ) : (
              <div className="space-y-2">
                {signatures.map((s) => {
                  const st = SIG_STATUS[s.status] || SIG_STATUS.sent;
                  const StIcon = st.icon;
                  return (
                    <div key={s.id} className="rounded-lg border p-2.5" data-testid={`signature-${s.id}`}>
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5">
                            <span className="truncate text-sm font-medium">{s.recipient_name}</span>
                            {s.envelope_id && <Badge variant="secondary" className="shrink-0 px-1.5 py-0 text-[10px] font-normal" data-testid={`sig-order-${s.id}`}>Signer {(s.order ?? 0) + 1} of {s.total_signers}</Badge>}
                          </div>
                          <div className="truncate text-[11px] text-muted-foreground">{s.recipient_email}</div>
                        </div>
                        <Badge className={`shrink-0 gap-1 font-normal ${st.cls}`} data-testid={`signature-status-${s.id}`}><StIcon className="h-3 w-3" />{st.label}</Badge>
                      </div>
                      <div className="mt-1.5 flex items-center gap-2 text-[11px] text-muted-foreground">
                        {s.countersigned_at ? <span>Completed {formatDateTime(s.countersigned_at)}</span>
                          : s.signed_at ? <span>Signed {formatDateTime(s.signed_at)}</span>
                          : s.viewed_at ? <span>Viewed {formatDateTime(s.viewed_at)}</span>
                          : <span>Sent {formatDateTime(s.sent_at)}</span>}
                      </div>
                      <details className="mt-1.5 group" data-testid={`audit-${s.id}`}>
                        <summary className="flex cursor-pointer list-none items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground">
                          <History className="h-3 w-3" /> Audit trail
                        </summary>
                        <ol className="mt-1.5 space-y-1 border-l pl-3">
                          {auditEvents(s).map((ev) => (
                            <li key={`${ev.label}-${ev.at}`} className="relative text-[11px] text-muted-foreground">
                              <span className="absolute -left-[13px] top-1 h-1.5 w-1.5 rounded-full bg-primary" />
                              <span className="text-foreground">{ev.label}</span> · {formatDateTime(ev.at)}
                            </li>
                          ))}
                        </ol>
                      </details>
                      <div className="mt-1.5 flex flex-wrap items-center gap-1">
                        {s.status === "awaiting_countersign" && (
                          <Button variant="default" size="sm" className="h-6 gap-1 px-1.5 text-[11px]" onClick={() => countersign(s.id)} data-testid={`countersign-${s.id}`}><PenLine className="h-3 w-3" /> Countersign</Button>
                        )}
                        {s.status !== "signed" && s.status !== "countersigned" && s.status !== "queued" && (
                          <Button variant="outline" size="sm" className="h-6 gap-1 px-1.5 text-[11px]" onClick={() => resendSignature(s.id)} data-testid={`resend-${s.id}`}><Send className="h-3 w-3" /> Resend</Button>
                        )}
                        {s.signed_document_id && (
                          <Button variant="outline" size="sm" className="h-6 gap-1 px-1.5 text-[11px]" onClick={() => downloadSigned(s)} data-testid={`signed-pdf-${s.id}`}><FileDown className="h-3 w-3" /> Signed PDF</Button>
                        )}
                        <Button variant="outline" size="sm" className="h-6 gap-1 px-1.5 text-[11px]" onClick={() => copyLink(s.sign_url)} data-testid={`copy-link-${s.id}`}><Copy className="h-3 w-3" /> Link</Button>
                        {s.status !== "signed" && s.status !== "countersigned" && <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[11px] text-muted-foreground hover:text-red-600" onClick={() => cancelSignature(s.id)} data-testid={`cancel-sig-${s.id}`}>Remove</Button>}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>
        </div>

        {/* Right: clause editor + preview */}
        <Tabs defaultValue="editor">
          <TabsList data-testid="builder-tabs">
            <TabsTrigger value="editor" data-testid="tab-editor">Clause editor</TabsTrigger>
            <TabsTrigger value="preview" data-testid="tab-preview">Preview</TabsTrigger>
          </TabsList>

          <TabsContent value="editor" className="mt-4 space-y-4">
            {clauses.map((c) => {
              const StanceIcon = STANCE_META[c.stance]?.icon || ShieldCheck;
              return (
                <Card key={c.key} className="rounded-xl border bg-card p-4" data-testid={`clause-${c.key}`}>
                  <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-foreground">{c.title}</span>
                      <Badge variant="secondary" className="font-normal">{c.category}</Badge>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <ToggleGroup
                        type="single"
                        value={c.stance}
                        onValueChange={(v) => v && setClauseStance(c.key, v)}
                        className="rounded-lg border bg-secondary/40 p-0.5"
                        data-testid={`stance-${c.key}`}
                      >
                        {STANCE_ORDER.map((s) => (
                          <ToggleGroupItem key={s} value={s} className="h-7 px-2 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid={`stance-${c.key}-${s}`}>
                            {STANCE_META[s].label}
                          </ToggleGroupItem>
                        ))}
                      </ToggleGroup>
                      <Button variant="ghost" size="sm" className="h-7 gap-1 px-2 text-xs" onClick={() => setCompareKey(c.key)} data-testid={`compare-${c.key}`}>
                        <Columns3 className="h-3.5 w-3.5" /> Compare
                      </Button>
                    </div>
                  </div>
                  <Textarea
                    value={c.text}
                    onChange={(e) => setClauseText(c.key, e.target.value)}
                    className="min-h-[92px] text-sm"
                    data-testid={`clause-text-${c.key}`}
                  />
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground"><StanceIcon className="h-3 w-3" />{STANCE_META[c.stance]?.label}</span>
                    <div className="ml-auto flex items-center gap-1.5">
                      <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={!!aiBusy} onClick={() => runAi(c, "draft")} data-testid={`ai-draft-${c.key}`}>
                        {aiBusy === c.key + "draft" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />} Draft
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={!!aiBusy} onClick={() => runAi(c, "soften")} data-testid={`ai-soften-${c.key}`}>
                        {aiBusy === c.key + "soften" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Feather className="h-3.5 w-3.5" />} Soften
                      </Button>
                      <Button variant="outline" size="sm" className="h-7 gap-1 px-2 text-xs" disabled={!!aiBusy} onClick={() => runAi(c, "tighten")} data-testid={`ai-tighten-${c.key}`}>
                        {aiBusy === c.key + "tighten" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Tighten
                      </Button>
                    </div>
                  </div>
                </Card>
              );
            })}
          </TabsContent>

          <TabsContent value="preview" className="mt-4">
            <Card className="rounded-xl border bg-card p-8" data-testid="document-preview">
              <h1 className="h-display text-2xl font-semibold text-foreground">{renderText(doc.title, variables)}</h1>
              <p className="mt-2 text-xs italic text-muted-foreground">These are configurable starting points, not legal advice. Have a qualified lawyer review before signing.</p>
              <div className="mt-6 space-y-5">
                {clauses.map((c) => (
                  <div key={c.key}>
                    <h3 className="text-base font-semibold text-primary">{renderText(c.title, variables)}</h3>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground">{renderText(c.text, variables)}</p>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      <Dialog open={historyOpen} onOpenChange={setHistoryOpen}>
        <DialogContent className="max-w-md" data-testid="version-history-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><History className="h-5 w-5 text-primary" /> Version history</DialogTitle></DialogHeader>
          <div className="max-h-[60vh] space-y-2 overflow-y-auto">
            {versions.map((v) => (
              <div key={v.id} className="flex items-center justify-between rounded-lg border p-3" data-testid={`version-${v.version}`}>
                <div>
                  <div className="text-sm font-semibold">Version {v.version}</div>
                  <div className="text-xs text-muted-foreground">{v.note}</div>
                  <div className="text-[11px] text-muted-foreground">{formatDateTime(v.created_at)} · {v.author_name}</div>
                </div>
                <div className="flex gap-1.5">
                  <Button variant="outline" size="sm" className="h-7 px-2 text-xs" onClick={async () => {
                    try {
                      const res = await api.get(`/builder-documents/${id}/export`, { params: { format: "pdf", version: v.version }, responseType: "blob" });
                      const url = window.URL.createObjectURL(new Blob([res.data]));
                      const a = document.createElement("a"); a.href = url; a.download = `${doc.title.replace(/[^a-zA-Z0-9]+/g, "_")}_v${v.version}.pdf`;
                      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
                    } catch { toast.error("Export failed"); }
                  }} data-testid={`version-export-${v.version}`}>PDF</Button>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Send for signature dialog */}
      <Dialog open={sigOpen} onOpenChange={setSigOpen}>
        <DialogContent className="max-w-md" data-testid="send-signature-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Send className="h-5 w-5 text-primary" /> Send for signature</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-xs text-muted-foreground">We'll send a secure signing link for version {doc.current_version}. You'll see when each party views and signs it.</p>
            <label className="flex cursor-pointer items-start gap-2 rounded-lg border bg-secondary/40 p-3">
              <Checkbox checked={sigForm.ordered} onCheckedChange={(v) => setSigForm((f) => ({ ...f, ordered: !!v, bulk: v ? false : f.bulk }))} className="mt-0.5" data-testid="sig-ordered" />
              <span className="text-sm">
                <span className="font-medium">Multiple signers, in order</span>
                <span className="block text-xs text-muted-foreground">Each signer is invited only after the previous one signs. Optional.</span>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-2 rounded-lg border bg-secondary/40 p-3">
              <Checkbox checked={sigForm.bulk} onCheckedChange={(v) => setSigForm((f) => ({ ...f, bulk: !!v, ordered: v ? false : f.ordered }))} className="mt-0.5" data-testid="sig-bulk" />
              <span className="text-sm">
                <span className="font-medium">Send to many at once (bulk)</span>
                <span className="block text-xs text-muted-foreground">Every recipient gets their own independent link, all at the same time.</span>
              </span>
            </label>

            {(sigForm.ordered || sigForm.bulk) ? (
              <div className="space-y-2" data-testid="sig-signers-list">
                {sigForm.signers.map((s, i) => (
                  <div key={i} className="flex items-center gap-2 rounded-lg border p-2" data-testid={`sig-signer-${i}`}>
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary/10 text-xs font-semibold text-primary">{sigForm.ordered ? i + 1 : "•"}</span>
                    <Input value={s.name} onChange={(e) => setSigner(i, "name", e.target.value)} placeholder="Name" className="h-8" data-testid={`sig-signer-name-${i}`} />
                    <Input type="email" value={s.email} onChange={(e) => setSigner(i, "email", e.target.value)} placeholder="email@company.com" className="h-8" data-testid={`sig-signer-email-${i}`} />
                    {sigForm.signers.length > 1 && (
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 shrink-0 text-muted-foreground hover:text-red-600" onClick={() => removeSigner(i)} data-testid={`sig-signer-remove-${i}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                    )}
                  </div>
                ))}
                <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={addSigner} data-testid="sig-add-signer"><UserPlus className="h-3.5 w-3.5" /> Add {sigForm.bulk ? "recipient" : "signer"}</Button>
              </div>
            ) : (
              <>
                <div><Label>Recipient name</Label><Input value={sigForm.recipient_name} onChange={(e) => setSigForm((f) => ({ ...f, recipient_name: e.target.value }))} placeholder="e.g. Acme Ventures" className="mt-1" data-testid="sig-recipient-name" /></div>
                <div><Label>Recipient email</Label><Input type="email" value={sigForm.recipient_email} onChange={(e) => setSigForm((f) => ({ ...f, recipient_email: e.target.value }))} placeholder="name@company.com" className="mt-1" data-testid="sig-recipient-email" /></div>
              </>
            )}

            <div><Label>Message (optional)</Label><Textarea value={sigForm.message} onChange={(e) => setSigForm((f) => ({ ...f, message: e.target.value }))} placeholder="A short note to the signer" className="mt-1" data-testid="sig-message" /></div>
            {!sigForm.ordered && !sigForm.bulk && (
              <label className="flex cursor-pointer items-start gap-2 rounded-lg border bg-secondary/40 p-3">
                <Checkbox checked={sigForm.require_countersign} onCheckedChange={(v) => setSigForm((f) => ({ ...f, require_countersign: !!v }))} className="mt-0.5" data-testid="sig-require-countersign" />
                <span className="text-sm">
                  <span className="font-medium">Require my countersignature</span>
                  <span className="block text-xs text-muted-foreground">After the counterparty signs, you'll be asked to countersign to finalise. Both signatures appear on the signed PDF.</span>
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSigOpen(false)}>Cancel</Button>
            <Button onClick={sendForSignature} disabled={sendingSig} className="gap-2" data-testid="sig-submit">
              {sendingSig ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Clause comparison dialog */}
      <Dialog open={!!compareKey} onOpenChange={(v) => !v && setCompareKey(null)}>
        <DialogContent className="max-w-4xl" data-testid="compare-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Columns3 className="h-5 w-5 text-primary" /> Compare wording - {compareTemplate?.title}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            {STANCE_ORDER.map((s) => {
              const StIcon = STANCE_META[s].icon;
              const active = compareClause?.stance === s;
              return (
                <div key={s} className={`flex flex-col rounded-xl border p-4 ${active ? "border-primary ring-1 ring-primary/30" : ""}`} data-testid={`compare-variant-${s}`}>
                  <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-foreground"><StIcon className="h-4 w-4 text-primary" />{STANCE_META[s].label}{active && <Check className="h-3.5 w-3.5 text-primary" />}</div>
                  <p className="flex-1 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">{renderText(compareTemplate?.variants?.[s], variables)}</p>
                  <Button size="sm" variant={active ? "secondary" : "default"} className="mt-3 w-full" disabled={active} onClick={() => applyVariant(s)} data-testid={`use-variant-${s}`}>
                    {active ? "Current" : "Use this"}
                  </Button>
                </div>
              );
            })}
          </div>
          <p className="mt-1 text-xs text-muted-foreground">Applying a version replaces this clause's text with the chosen stance's wording. Any manual edits to this clause will be overwritten.</p>
        </DialogContent>
      </Dialog>
    </AppShell>
  );
}
